import { useToast } from '@chakra-ui/react';
import {
  CareerApplicationSubmission,
  useCareerApplicationMutation,
} from 'src/api/mutations';
import {
  PublicFormErrorReason,
  classifyPublicFormError,
} from 'src/utils/publicFormErrors';

type SubmitResult =
  | { success: true }
  | { success: false; reason: PublicFormErrorReason };

const errorToastCopy: Record<
  PublicFormErrorReason,
  { title: string; description: string; status: 'warning' | 'error' }
> = {
  rate_limited: {
    title: 'Too many requests',
    description: 'Please try again in a few minutes.',
    status: 'warning',
  },
  invalid_data: {
    title: 'Invalid form data',
    description: 'Please check your entries and try again.',
    status: 'error',
  },
  verification_failed: {
    title: 'Verification failed',
    description: 'Please complete the verification and try again.',
    status: 'error',
  },
  unknown: {
    title: 'Error submitting application',
    description: 'Please try again or contact us directly at jobs@yapamar.com',
    status: 'error',
  },
};

export const useCareerApplication = () => {
  const toast = useToast();
  const applicationMutation = useCareerApplicationMutation();

  const submitApplication = async (
    data: CareerApplicationSubmission,
  ): Promise<SubmitResult> => {
    try {
      await applicationMutation.mutateAsync(data);

      toast({
        title: 'Application submitted!',
        description: "Thank you — we'll be in touch.",
        status: 'success',
        isClosable: true,
        variant: 'solid',
      });
      return { success: true };
    } catch (error) {
      const reason = classifyPublicFormError(error);

      toast({
        ...errorToastCopy[reason],
        isClosable: true,
        variant: 'solid',
      });
      return { success: false, reason };
    }
  };

  return {
    submitApplication,
    isLoading: applicationMutation.isLoading,
  };
};
